import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

export type AutocompleteOption = {
  label: string;
  value: string;
};

export type AutocompleteProps = {
  name: string;
  value?: string;
  placeholder?: string;
  loadOptions: (query: string) => Promise<AutocompleteOption[]>;
  onChange?: (event: { target: { name: string; label: string | null; value: string | null } }) => void;
  onBlur?: (event: { target: { name: string; value: string | null } }) => void;
  className?: string;
  invalid?: boolean;
  minSearchLength?: number;
};

const Autocomplete = ({
  name,
  value,
  placeholder,
  loadOptions,
  onChange,
  onBlur,
  className,
  invalid,
  minSearchLength = 2,
}: AutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [popupStyle, setPopupStyle] = useState({ left: 0, top: 0, width: 0 });
  const requestId = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<AutocompleteOption | null>(null);
  const normalizedQuery = inputValue.trim();

  const visibleOptions = useMemo(() => options.slice(0, 80), [options]);

  useEffect(() => {
    if (value && selectedOptionRef.current?.value === value) {
      setInputValue(selectedOptionRef.current.label);
      return;
    }

    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (normalizedQuery.length < minSearchLength) {
      setOptions([]);
      setLoading(false);
      return;
    }

    const nextRequestId = requestId.current + 1;
    requestId.current = nextRequestId;
    setLoading(true);

    const timer = window.setTimeout(() => {
      loadOptions(normalizedQuery)
        .then((nextOptions) => {
          if (requestId.current === nextRequestId) {
            setOptions(nextOptions);
            setHighlightedIndex(0);
          }
        })
        .catch(() => {
          if (requestId.current === nextRequestId) {
            setOptions([]);
          }
        })
        .finally(() => {
          if (requestId.current === nextRequestId) {
            setLoading(false);
          }
        });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadOptions, minSearchLength, normalizedQuery]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!wrapperRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const updatePopupPosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      setPopupStyle({
        left: rect.left,
        top: rect.bottom + 4,
        width: rect.width,
      });
    };

    updatePopupPosition();
    window.addEventListener('resize', updatePopupPosition);
    window.addEventListener('scroll', updatePopupPosition, true);

    return () => {
      window.removeEventListener('resize', updatePopupPosition);
      window.removeEventListener('scroll', updatePopupPosition, true);
    };
  }, [open]);

  const emitChange = (option: AutocompleteOption | null) => {
    onChange?.({
      target: {
        name,
        label: option?.label || null,
        value: option?.value || null,
      },
    });
  };

  const selectOption = (option: AutocompleteOption) => {
    selectedOptionRef.current = option;
    setInputValue(option.label);
    setOpen(false);
    emitChange(option);
    onBlur?.({ target: { name, value: option.value } });
  };

  return (
    <div ref={wrapperRef} className={classNames('relative', className)}>
      <input
        ref={inputRef}
        name={name}
        value={inputValue}
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        //aria-expanded={open}
        aria-invalid={invalid}
        className={classNames(
          'h-11 w-full rounded-[6px] border bg-white px-3 text-sm text-zinc-800 transition outline-none placeholder:text-zinc-400',
          invalid ? 'border-red-500 focus:border-red-500' : 'border-zinc-200 focus:border-zinc-400',
        )}
        onFocus={() => setOpen(true)}
        onBlur={() => onBlur?.({ target: { name, value: inputValue || null } })}
        onChange={(event) => {
          const nextValue = event.target.value;
          selectedOptionRef.current = null;
          setInputValue(nextValue);
          setOpen(true);
          emitChange(nextValue ? { label: nextValue, value: '' } : null);
        }}
        onKeyDown={(event) => {
          if (!open || visibleOptions.length === 0) {
            return;
          }

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((current) => (current + 1) % visibleOptions.length);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((current) => (current > 0 ? current - 1 : visibleOptions.length - 1));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            selectOption(visibleOptions[highlightedIndex]);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />

      {open &&
        normalizedQuery.length >= minSearchLength &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popupRef}
            style={popupStyle}
            className="fixed z-[60] max-h-72 overflow-y-auto rounded-[6px] border border-zinc-200 bg-white py-2 shadow-lg"
            data-lenis-prevent
          >
            {loading ? (
              <div className="px-3 py-2 text-sm text-zinc-500">Loading...</div>
            ) : visibleOptions.length > 0 ? (
              visibleOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  className={classNames(
                    'block w-full px-3 py-2 text-left text-sm text-zinc-800 outline-none hover:bg-gray-100',
                    highlightedIndex === index && 'bg-gray-100',
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-zinc-500">No results found</div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Autocomplete;
